/**
 * Project Pictures 1.0.
 *
 * One library per project. location_id null is an overall project picture.
 * A location picture points at a location on that same project.
 * Location galleries are filters, not copies.
 */

import { supabase } from "../supabase";
import { assertCan, can, CAPABILITY } from "./permissions";
import { resolveOrganizationIdForQueries } from "./orgQueryScope";
import { loadUsersByAuthIdsForOrg } from "./scopedQueries";
import { isBrowserReportedOnline, isOffline } from "./connectivity";
import {
  assertProjectOperationallyWritable,
  OPERATIONAL_LOCK_USER_MESSAGE,
} from "./operationalLifecycle";

export const PROJECT_PICTURES_BUCKET = "project-pictures";
export const MAX_PICTURE_BYTES = 12 * 1024 * 1024;
export const PICTURE_NOTE_PROMPT =
  "Would you like to add a note to this picture?";
export const OFFLINE_PICTURE_MESSAGE =
  "You're offline. This picture was not saved. Reconnect and try again.";
export const OFFLINE_NOTE_MESSAGE =
  "You're offline. This note was not saved. Reconnect and try again.";
export const OFFLINE_COMMENT_REMOVE_MESSAGE =
  "You're offline. This comment was not removed. Reconnect and try again.";

/**
 * Comment and upload identity for a later offline sync:
 * comment_id and picture_id stay immutable, created_by is the auth user id
 * of the person who wrote the note or queued the capture, created_at is the
 * timestamp, source is typed or voice, and is_active false is the void.
 * Rows are not deleted. Display names are resolved from the membership and
 * profile for that auth id. They are not copied onto the picture or comment.
 */

const PROJECT_EDIT_DENIED = "You do not have permission to edit this project.";
const PROMPT_KEY = "cp-picture-ashley-prompt-off";
const SIGNED_URL_SECONDS = 60 * 60;

const MIME_EXTENSIONS = Object.freeze({
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/heic": "heic",
  "image/heif": "heif",
});

function requirePictureView(actor) {
  assertCan(actor, CAPABILITY.PROJECT_VIEW);
}

function requirePictureEdit(actor) {
  assertCan(actor, CAPABILITY.PROJECT_EDIT, {}, undefined, PROJECT_EDIT_DENIED);
}

export function createPictureId() {
  if (typeof window !== "undefined" && window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const rand = Math.floor(Math.random() * 16);
    const value = char === "x" ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
}

function assertOnline(message) {
  if (isOffline() || !isBrowserReportedOnline()) {
    throw new Error(message);
  }
}

async function requireActorOrganizationId(actor) {
  const organizationId = await resolveOrganizationIdForQueries(actor, supabase);
  if (!organizationId) {
    throw new Error("Organization is required.");
  }
  return organizationId;
}

async function loadWritableProject(actor, projectId, { requireEdit = true } = {}) {
  if (requireEdit) requirePictureEdit(actor);
  else requirePictureView(actor);
  const organizationId = await requireActorOrganizationId(actor);

  const { data: project, error } = await supabase
    .from("projects")
    .select("project_id, organization_id, status, is_active, client_id")
    .eq("project_id", projectId)
    .eq("organization_id", organizationId)
    .maybeSingle();

  if (error) throw error;
  if (!project || project.organization_id !== organizationId) {
    throw new Error("Project not found.");
  }

  let client = null;
  if (project.client_id) {
    const { data, error: clientError } = await supabase
      .from("clients")
      .select("client_id, archived_at, client_status, organization_id")
      .eq("client_id", project.client_id)
      .eq("organization_id", organizationId)
      .maybeSingle();

    if (clientError) throw clientError;
    if (!data || data.organization_id !== organizationId) {
      throw new Error(OPERATIONAL_LOCK_USER_MESSAGE);
    }
    client = data;
  }

  assertProjectOperationallyWritable(project, client);
  return { organizationId, projectId: project.project_id };
}

export function choosePictureAuthorName({ employeeName, displayName, legacyName } = {}) {
  const candidates = [employeeName, displayName, legacyName];
  for (const value of candidates) {
    const name = String(value || "").trim();
    if (name) return name;
  }
  return "";
}

export function pictureAuthorLabel(userId, names) {
  if (!userId) return "Crew";
  const name = String(names?.[userId] || "").trim();
  return name || "Crew";
}

/**
 * Membership employee_name is the name CrewPilot shows for that person in
 * the organization. profiles.display_name is the identity fallback. The
 * users row is only the compatibility mirror. created_by stays the auth id.
 */
export async function loadPictureAuthorNames(actor, userIds) {
  requirePictureView(actor);
  const organizationId = await requireActorOrganizationId(actor);
  const ids = [...new Set((userIds || []).filter(Boolean))];
  const names = {};
  if (!ids.length) return names;

  const { data: profiles, error: profileError } = await supabase
    .from("profiles")
    .select("id, supabase_user_id, display_name")
    .in("supabase_user_id", ids);
  if (profileError) throw profileError;

  const profileByAuth = new Map();
  (profiles || []).forEach((row) => {
    if (row?.supabase_user_id) profileByAuth.set(row.supabase_user_id, row);
  });

  const profileIds = [...profileByAuth.values()].map((row) => row.id).filter(Boolean);
  let members = [];
  if (profileIds.length) {
    const { data, error } = await supabase
      .from("organization_members")
      .select("profile_id, employee_name")
      .eq("organization_id", organizationId)
      .eq("active", true)
      .in("profile_id", profileIds);
    if (error) throw error;
    members = data || [];
  }

  const memberNameByProfile = new Map(
    members.map((row) => [row.profile_id, row.employee_name]),
  );
  const unresolved = [];
  ids.forEach((id) => {
    const profile = profileByAuth.get(id);
    const name = choosePictureAuthorName({
      employeeName: profile ? memberNameByProfile.get(profile.id) : "",
      displayName: profile?.display_name,
    });
    if (name) names[id] = name;
    else unresolved.push(id);
  });

  if (unresolved.length) {
    const users = await loadUsersByAuthIdsForOrg(
      supabase,
      actor,
      unresolved,
      "supabase_user_id, employee_name",
    );
    users.forEach((user) => {
      const name = choosePictureAuthorName({ legacyName: user.employee_name });
      if (name && user.supabase_user_id && !names[user.supabase_user_id]) {
        names[user.supabase_user_id] = name;
      }
    });
  }

  return names;
}

export function canVoidPictureComment(actor, comment) {
  if (!actor || !comment || comment.is_active === false) return false;
  const authorId = comment.created_by || null;
  const actorId = actor.supabase_user_id || null;
  if (authorId && actorId && authorId === actorId) return true;
  return can(actor, CAPABILITY.PROJECT_EDIT);
}

export function photoCountLabel(count) {
  const total = Number(count) || 0;
  return `${total} photo${total === 1 ? "" : "s"}`;
}

export function resolveImageMime(file) {
  const type = String(file?.type || "").toLowerCase();
  if (MIME_EXTENSIONS[type]) return type;

  const name = String(file?.name || "").toLowerCase();
  if (name.endsWith(".jpg") || name.endsWith(".jpeg")) return "image/jpeg";
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".webp")) return "image/webp";
  if (name.endsWith(".heic")) return "image/heic";
  if (name.endsWith(".heif")) return "image/heif";
  return null;
}

export function validatePictureFile(file) {
  if (!file) {
    return { valid: false, error: "Choose a photo." };
  }

  const contentType = resolveImageMime(file);
  if (!contentType) {
    return { valid: false, error: "Use a JPEG, PNG, WebP, or HEIC photo." };
  }

  if (!file.size) {
    return { valid: false, error: "That photo is empty." };
  }

  if (file.size > MAX_PICTURE_BYTES) {
    return { valid: false, error: "Photo must be 12 MB or smaller." };
  }

  return { valid: true, contentType };
}

export function resolvePictureLocation({ locationId, locations, projectId }) {
  if (!locationId) return null;

  const match = (locations || []).find(
    (row) => row?.project_location_id === locationId,
  );
  if (!match) {
    throw new Error("That location is not on this project.");
  }
  if (projectId && match.project_id && match.project_id !== projectId) {
    throw new Error("That location is not on this project.");
  }
  return match.project_location_id;
}

export function summarizePictures(rows = []) {
  const byLocation = new Map();
  let overall = 0;
  let total = 0;

  (rows || []).forEach((row) => {
    if (row?.is_active === false) return;
    total += 1;
    if (!row?.location_id) {
      overall += 1;
      return;
    }
    byLocation.set(row.location_id, (byLocation.get(row.location_id) || 0) + 1);
  });

  return { total, overall, byLocation };
}

export function shouldAskForPictureNote() {
  try {
    return sessionStorage.getItem(PROMPT_KEY) !== "1";
  } catch {
    return true;
  }
}

export function suppressPictureNotePrompt() {
  try {
    sessionStorage.setItem(PROMPT_KEY, "1");
  } catch {
    // Session preference is optional. The photo is already saved.
  }
}

export function pictureStoragePath({ organizationId, projectId, pictureId, contentType }) {
  const ext = MIME_EXTENSIONS[contentType] || "jpg";
  return `${organizationId}/${projectId}/${pictureId}.${ext}`;
}

export function pictureThumbnailPath({ organizationId, projectId, pictureId }) {
  return `${organizationId}/${projectId}/${pictureId}_thumb.jpg`;
}

export async function createThumbnailBlob(file) {
  if (typeof document === "undefined" || typeof createImageBitmap !== "function") {
    return null;
  }

  try {
    const bitmap = await createImageBitmap(file);
    const edge = Math.max(bitmap.width, bitmap.height) || 1;
    const scale = Math.min(1, 640 / edge);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext("2d");
    if (!context) return null;
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();
    const blob = await new Promise((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", 0.7);
    });
    return blob || null;
  } catch {
    return null;
  }
}

async function signPaths(paths) {
  const unique = [...new Set((paths || []).filter(Boolean))];
  if (!unique.length) return {};

  const { data, error } = await supabase.storage
    .from(PROJECT_PICTURES_BUCKET)
    .createSignedUrls(unique, SIGNED_URL_SECONDS);

  if (error) throw error;

  const byPath = {};
  (data || []).forEach((item) => {
    if (item?.path && item?.signedUrl) byPath[item.path] = item.signedUrl;
  });
  return byPath;
}

async function removeUploaded(paths) {
  const unique = [...new Set((paths || []).filter(Boolean))];
  if (!unique.length) return;
  try {
    await supabase.storage.from(PROJECT_PICTURES_BUCKET).remove(unique);
  } catch {
    // The picture row was not inserted. A leftover object is not shown as saved.
  }
}

async function loadActivePicture(actor, pictureId) {
  requirePictureView(actor);
  const organizationId = await requireActorOrganizationId(actor);

  const { data, error } = await supabase
    .from("project_pictures")
    .select("*")
    .eq("picture_id", pictureId)
    .eq("organization_id", organizationId)
    .eq("is_active", true)
    .maybeSingle();

  if (error) throw error;
  if (!data) throw new Error("That picture is not in this project.");
  return data;
}

export async function loadProjectPictureSummaries(actor, projectId) {
  requirePictureView(actor);
  if (isOffline()) return summarizePictures([]);
  const organizationId = await requireActorOrganizationId(actor);

  const { data, error } = await supabase
    .from("project_pictures")
    .select("picture_id, location_id, is_active")
    .eq("organization_id", organizationId)
    .eq("project_id", projectId)
    .eq("is_active", true);

  if (error) throw error;
  return summarizePictures(data || []);
}

export async function loadProjectPictures(actor, projectId) {
  requirePictureView(actor);
  if (isOffline()) return [];
  const organizationId = await requireActorOrganizationId(actor);

  const { data, error } = await supabase
    .from("project_pictures")
    .select("*")
    .eq("organization_id", organizationId)
    .eq("project_id", projectId)
    .eq("is_active", true)
    .order("created_at", { ascending: false });

  if (error) throw error;
  const rows = data || [];
  const signed = await signPaths(rows.map((row) => row.thumbnail_path || row.storage_path));
  return rows.map((row) => ({
    ...row,
    previewUrl: signed[row.thumbnail_path || row.storage_path] || "",
  }));
}

export async function loadProjectPictureDetail(actor, pictureId) {
  const picture = await loadActivePicture(actor, pictureId);
  const signed = await signPaths([picture.storage_path]);

  const { data, error } = await supabase
    .from("project_picture_comments")
    .select("*")
    .eq("organization_id", picture.organization_id)
    .eq("picture_id", pictureId)
    .eq("is_active", true)
    .order("created_at", { ascending: true });

  if (error) throw error;

  return {
    picture: {
      ...picture,
      imageUrl: signed[picture.storage_path] || "",
    },
    comments: data || [],
  };
}

export async function addProjectPicture(actor, projectId, file, options = {}) {
  assertOnline(OFFLINE_PICTURE_MESSAGE);

  const validation = validatePictureFile(file);
  if (!validation.valid) throw new Error(validation.error);

  const locationId = resolvePictureLocation({
    locationId: options.locationId || null,
    locations: options.locations || [],
    projectId,
  });

  const { organizationId } = await loadWritableProject(actor, projectId);
  const pictureId = options.pictureId || createPictureId();
  const retainRemoteObjects = Boolean(options.pictureId);
  const storagePath = pictureStoragePath({
    organizationId,
    projectId,
    pictureId,
    contentType: validation.contentType,
  });
  const uploaded = [];

  try {
    const { error: uploadError } = await supabase.storage
      .from(PROJECT_PICTURES_BUCKET)
      .upload(storagePath, file, {
        contentType: validation.contentType,
        upsert: retainRemoteObjects,
        cacheControl: "3600",
      });
    if (uploadError) throw uploadError;
    uploaded.push(storagePath);

    let thumbnailPath = null;
    const thumb = options.thumbnailBlob || (await createThumbnailBlob(file));
    if (thumb) {
      const candidate = pictureThumbnailPath({ organizationId, projectId, pictureId });
      const { error: thumbError } = await supabase.storage
        .from(PROJECT_PICTURES_BUCKET)
        .upload(candidate, thumb, {
          contentType: "image/jpeg",
          upsert: retainRemoteObjects,
          cacheControl: "3600",
        });
      if (!thumbError) {
        thumbnailPath = candidate;
        uploaded.push(candidate);
      }
    }

    if (typeof options.onStorageUploaded === "function") {
      options.onStorageUploaded();
    }

    const capturedAt = options.capturedAt
      || (file.lastModified ? new Date(file.lastModified).toISOString() : null);

    const { data, error } = await supabase
      .from("project_pictures")
      .insert({
        picture_id: pictureId,
        organization_id: organizationId,
        project_id: projectId,
        location_id: locationId,
        storage_path: storagePath,
        thumbnail_path: thumbnailPath,
        file_name: file.name || `photo.${MIME_EXTENSIONS[validation.contentType]}`,
        content_type: validation.contentType,
        byte_size: file.size,
        captured_at: capturedAt,
        created_by: actor?.supabase_user_id || null,
        is_active: true,
      })
      .select("*")
      .single();

    if (error) {
      const duplicate = error.code === "23505" || /duplicate key/i.test(error.message || "");
      if (!(retainRemoteObjects && duplicate)) throw error;
      return {
        picture_id: pictureId,
        organization_id: organizationId,
        project_id: projectId,
        location_id: locationId,
        storage_path: storagePath,
        thumbnail_path: thumbnailPath,
        created_by: actor?.supabase_user_id || null,
        captured_at: capturedAt,
        is_active: true,
      };
    }
    return data;
  } catch (error) {
    if (!retainRemoteObjects) await removeUploaded(uploaded);
    throw error;
  }
}

export async function addProjectPictureComment(actor, pictureId, { body, source = "typed", commentId = null, createdAt = null } = {}) {
  const trimmed = String(body || "").trim();
  if (!trimmed) throw new Error("Enter a note.");
  if (source !== "typed" && source !== "voice") {
    throw new Error("Unsupported note.");
  }

  assertOnline(OFFLINE_NOTE_MESSAGE);
  const picture = await loadActivePicture(actor, pictureId);
  await loadWritableProject(actor, picture.project_id);

  const { data, error } = await supabase
    .from("project_picture_comments")
    .insert({
      ...(commentId ? { comment_id: commentId } : {}),
      organization_id: picture.organization_id,
      project_id: picture.project_id,
      picture_id: pictureId,
      body: trimmed,
      source,
      ...(createdAt ? { created_at: createdAt } : {}),
      created_by: actor?.supabase_user_id || null,
      is_active: true,
    })
    .select("*")
    .single();

  if (error) {
    const duplicate = error.code === "23505" || /duplicate key/i.test(error.message || "");
    if (!(commentId && duplicate)) throw error;
    return {
      comment_id: commentId,
      organization_id: picture.organization_id,
      project_id: picture.project_id,
      picture_id: pictureId,
      body: trimmed,
      source,
      created_at: createdAt,
      created_by: actor?.supabase_user_id || null,
      is_active: true,
    };
  }
  return data;
}

export async function voidProjectPictureComment(actor, commentId) {
  assertOnline(OFFLINE_COMMENT_REMOVE_MESSAGE);
  requirePictureView(actor);
  const organizationId = await requireActorOrganizationId(actor);

  const { data: comment, error: loadError } = await supabase
    .from("project_picture_comments")
    .select("*")
    .eq("comment_id", commentId)
    .eq("organization_id", organizationId)
    .eq("is_active", true)
    .maybeSingle();

  if (loadError) throw loadError;
  if (!comment) throw new Error("That comment is not on this picture.");
  if (!canVoidPictureComment(actor, comment)) {
    throw new Error("You do not have permission to remove this comment.");
  }

  const isAuthor = Boolean(
    comment.created_by && comment.created_by === actor?.supabase_user_id,
  );
  await loadWritableProject(actor, comment.project_id, { requireEdit: !isAuthor });

  const { error } = await supabase
    .from("project_picture_comments")
    .update({
      is_active: false,
      voided_at: new Date().toISOString(),
      voided_by: actor?.supabase_user_id || null,
    })
    .eq("comment_id", commentId)
    .eq("organization_id", organizationId)
    .eq("is_active", true);

  if (error) throw error;
  return { ...comment, is_active: false };
}

export async function reassignProjectPicture(actor, pictureId, { locationId, locations } = {}) {
  const picture = await loadActivePicture(actor, pictureId);
  await loadWritableProject(actor, picture.project_id);
  const nextLocationId = resolvePictureLocation({
    locationId: locationId || null,
    locations: locations || [],
    projectId: picture.project_id,
  });

  const { data, error } = await supabase
    .from("project_pictures")
    .update({ location_id: nextLocationId })
    .eq("picture_id", pictureId)
    .eq("organization_id", picture.organization_id)
    .eq("is_active", true)
    .select("*")
    .single();

  if (error) throw error;
  return data;
}

export async function voidProjectPicture(actor, pictureId) {
  const picture = await loadActivePicture(actor, pictureId);
  await loadWritableProject(actor, picture.project_id);

  const { error } = await supabase
    .from("project_pictures")
    .update({
      is_active: false,
      voided_at: new Date().toISOString(),
      voided_by: actor?.supabase_user_id || null,
    })
    .eq("picture_id", pictureId)
    .eq("organization_id", picture.organization_id)
    .eq("is_active", true);

  if (error) throw error;
}
